/**
 * Petits outils partagés de la messagerie. Sans dépendance à React.
 */
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

/* ── Système visuel ────────────────────────────────────────────────────────
 * Trois tailles, une échelle de 4 px, deux rayons. Le vert est celui de la
 * sidebar CBConnect ; le reste est une échelle de gris.
 */
export const T = {
  vert: '#1a6b3c',
  vertFonce: '#155731',
  vertPale: '#e8f5ed',
  vertXpale: '#f0faf4',
  texte: '#111827',
  texte2: '#374151',
  muet: '#6b7280',
  faible: '#9ca3af',
  bord: '#e5e7eb',
  fond: '#f9fafb',
  fondBulle: '#f3f4f6',
  blanc: '#ffffff',
  ambre: '#b45309',
  ambreFond: '#fef3c7',
  rouge: '#b91c1c',
  rougeFond: '#fee2e2',
  bleu: '#1d4ed8',
  taille: { corps: 15, meta: 13, titre: 17, mini: 12 },
  rayon: { carte: 8, champ: 6 },
};

const AVATARS = ['#1a6b3c', '#7c3aed', '#b45309', '#1d4ed8', '#be123c', '#0f766e', '#4338ca', '#0369a1'];

export const couleurAvatar = (cle = '') => {
  let somme = 0;
  for (let i = 0; i < cle.length; i += 1) somme = (somme * 31 + cle.charCodeAt(i)) >>> 0;
  return AVATARS[somme % AVATARS.length];
};

/* ── Dates ─────────────────────────────────────────────────────────────── */

const memeJour = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const heure = (iso) => (iso
  ? new Date(iso).toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' }) : '');

/** « Aujourd'hui », « Hier », sinon la date longue. */
export const libelleJour = (iso) => {
  const d = new Date(iso);
  const auj = new Date();
  const hier = new Date(); hier.setDate(auj.getDate() - 1);
  if (memeJour(d, auj)) return i18n.t('conversations:dates.aujourdhui');
  if (memeJour(d, hier)) return i18n.t('conversations:dates.hier');
  return d.toLocaleDateString(localeIntl(), { weekday: 'long', day: 'numeric', month: 'long', year: d.getFullYear() !== auj.getFullYear() ? 'numeric' : undefined });
};

/** Heure si aujourd'hui, jour sinon - pour la liste de conversations. */
export const horodatageCourt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const auj = new Date();
  if (memeJour(d, auj)) return heure(iso);
  const hier = new Date(); hier.setDate(auj.getDate() - 1);
  if (memeJour(d, hier)) return i18n.t('conversations:dates.hier');
  const jours = (auj - d) / 86400000;
  if (jours < 7) return d.toLocaleDateString(localeIntl(), { weekday: 'short' });
  return d.toLocaleDateString(localeIntl(), { day: '2-digit', month: '2-digit' });
};

export const derniereVueLibelle = (iso) => {
  if (!iso) return i18n.t('conversations:presence.horsLigne');
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return i18n.t('conversations:presence.vuInstant');
  if (min < 60) return i18n.t('conversations:presence.vuMinutes', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t('conversations:presence.vuHeures', { n: h });
  return i18n.t('conversations:presence.vuLe', { date: new Date(iso).toLocaleDateString(localeIntl()) });
};

/* ── Groupement des messages ────────────────────────────────────────────
 * Un message ouvre un groupe s'il change d'auteur, de jour, ou si plus de 5
 * minutes le séparent du précédent. Un message système est toujours seul.
 */
const CINQ_MINUTES = 5 * 60 * 1000;

export const construireLignes = (messages, premiereNonLue) => {
  const lignes = [];
  let precedent = null;
  let separateurNonLuPose = false;
  messages.forEach((m) => {
    const d = new Date(m.creeLe);
    if (!precedent || !memeJour(new Date(precedent.creeLe), d)) {
      lignes.push({ type: 'jour', cle: `jour-${m.sequence}`, libelle: libelleJour(m.creeLe) });
    }
    if (premiereNonLue != null && !separateurNonLuPose && m.sequence >= premiereNonLue && !m.parMoi) {
      lignes.push({ type: 'nonlus', cle: 'nonlus' });
      separateurNonLuPose = true;
    }
    const systeme = m.type === 'SYSTEME';
    const teteDeGroupe = systeme || !precedent || precedent.type === 'SYSTEME'
      || precedent.auteurTrackingId !== m.auteurTrackingId
      || (d - new Date(precedent.creeLe)) > CINQ_MINUTES
      || !memeJour(new Date(precedent.creeLe), d);
    lignes.push({ type: 'message', cle: m.clientUuid || m.messageTrackingId, message: m, teteDeGroupe });
    precedent = m;
  });
  return lignes;
};

/* ── Liens ────────────────────────────────────────────────────────────────
 * Détection d'URL puis rendu en nœuds React par le composant appelant :
 * jamais d'injection de HTML.
 */
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

export const decouperLiens = (texte = '') => {
  const parts = [];
  let dernier = 0;
  texte.replace(URL_RE, (url, _g, index) => {
    if (index > dernier) parts.push({ type: 'texte', valeur: texte.slice(dernier, index) });
    // Ponctuation finale exclue du lien : « voir https://x.fr. » ne doit pas casser l'URL.
    const m = url.match(/^(.*?)([.,;:!?)]*)$/);
    parts.push({ type: 'lien', valeur: m[1] });
    if (m[2]) parts.push({ type: 'texte', valeur: m[2] });
    dernier = index + url.length;
    return url;
  });
  if (dernier < texte.length) parts.push({ type: 'texte', valeur: texte.slice(dernier) });
  return parts;
};

/* ── Mentions ───────────────────────────────────────────────────────────── */

/**
 * Même marqueur que la discussion de dossier : `@[Nom](utilisateur:uuid)`. Le nom
 * n'est qu'un affichage, l'UUID fait foi - le serveur réécrit le nom à partir de
 * l'identité réelle avant d'enregistrer, et ramène en texte plat toute mention
 * vers quelqu'un qui n'est pas membre de la conversation.
 */
const MENTION_RE = /@\[([^\]\r\n]{1,120})\]\((utilisateur|role):([A-Za-z0-9_-]{1,60})\)/g;

export const marqueurMention = (membre) => `@[${membre.nomComplet}](utilisateur:${membre.utilisateurTrackingId})`;

/** Découpe un texte en fragments `texte` / `mention` ({ valeur, utilisateurTrackingId }). */
export const decouperMentions = (texte = '') => {
  const parts = [];
  let dernier = 0;
  texte.replace(MENTION_RE, (m, nom, type, id, index) => {
    if (index > dernier) parts.push({ type: 'texte', valeur: texte.slice(dernier, index) });
    parts.push({ type: 'mention', valeur: nom, utilisateurTrackingId: type === 'utilisateur' ? id : null });
    dernier = index + m.length;
    return m;
  });
  if (dernier < texte.length) parts.push({ type: 'texte', valeur: texte.slice(dernier) });
  return parts;
};

/* ── Dossiers cités ─────────────────────────────────────────────────────── */

/**
 * `#[2026/0363/CI](sinistre:uuid)` : un dossier désigné par son identifiant.
 *
 * Un numéro écrit à la main doit être RECHERCHÉ au clic, et la recherche ne mène
 * au dossier que si elle ne trouve qu'un résultat exact - sinon l'agent tombe sur
 * la liste des sinistres. Le marqueur porte l'identifiant de suivi, stable d'une
 * version du dossier à l'autre : le clic ouvre le dossier sans rien résoudre. Le
 * serveur réécrit le numéro affiché depuis le dossier réel.
 */
const DOSSIER_RE = /#\[([^\]\r\n]{1,60})\]\(sinistre:([0-9a-fA-F-]{36})\)/g;

export const marqueurDossier = (sinistre) => `#[${sinistre.numeroSinistreLocal}](sinistre:${sinistre.sinistreTrackingId})`;

/** Découpe un texte en fragments `texte` / `dossier` ({ valeur: numéro, sinistreTrackingId }). */
export const decouperDossiers = (texte = '') => {
  const parts = [];
  let dernier = 0;
  texte.replace(DOSSIER_RE, (m, numero, id, index) => {
    if (index > dernier) parts.push({ type: 'texte', valeur: texte.slice(dernier, index) });
    parts.push({ type: 'dossier', valeur: numero, sinistreTrackingId: id });
    dernier = index + m.length;
    return m;
  });
  if (dernier < texte.length) parts.push({ type: 'texte', valeur: texte.slice(dernier) });
  return parts;
};

/** Dossiers désignés par marqueur dans un texte, sans doublon. */
export const dossiersMarques = (texte = '') => {
  const vus = new Map();
  (texte || '').replace(DOSSIER_RE, (m, numero, id) => {
    if (!vus.has(id)) vus.set(id, { numero, sinistreTrackingId: id });
    return m;
  });
  return [...vus.values()];
};

/**
 * `@[Awa Koffi](utilisateur:…)` → `@Awa Koffi`, `#[2026/0363/CI](sinistre:…)` → `2026/0363/CI`,
 * pour les endroits sans puce (citation, recherche).
 */
export const texteSansMarqueurs = (texte = '') => texte.replace(MENTION_RE, '@$1').replace(DOSSIER_RE, '$1');

/**
 * Comparaison sans accents ni casse, pour filtrer les membres à la frappe :
 * « kodjo » doit trouver « Kodjo », et « mensa » trouver « Mensah ».
 */
export const normaliser = (s = '') => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/* ── Brouillons ─────────────────────────────────────────────────────────── */

const CLE_BROUILLON = (id) => `cb.conversation.brouillon.${id}`;
export const lireBrouillon = (id) => { try { return localStorage.getItem(CLE_BROUILLON(id)) || ''; } catch { return ''; } };
export const ecrireBrouillon = (id, texte) => {
  try {
    if (texte) localStorage.setItem(CLE_BROUILLON(id), texte);
    else localStorage.removeItem(CLE_BROUILLON(id));
  } catch { /* stockage indisponible */ }
};

const CLE_ASTUCE = 'cb.conversation.astuce-entree';
export const astuceEntreeVue = () => { try { return localStorage.getItem(CLE_ASTUCE) === '1'; } catch { return true; } };
export const marquerAstuceEntreeVue = () => { try { localStorage.setItem(CLE_ASTUCE, '1'); } catch { /* ignore */ } };

/* ── Fichiers ───────────────────────────────────────────────────────────── */

export const MIMES_ACCEPTES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const TAILLE_MAX = 10 * 1024 * 1024;

export const tailleLisible = (octets) => {
  if (octets < 1024) return i18n.t('conversations:tailles.octets', { n: octets });
  if (octets < 1024 * 1024) return i18n.t('conversations:tailles.ko', { n: Math.round(octets / 1024) });
  return i18n.t('conversations:tailles.mo', { n: (octets / (1024 * 1024)).toFixed(1) });
};

/**
 * Réduit une image côté navigateur : bord max 1600 px, JPEG qualité 0,82,
 * ré-encodage en boucle jusqu'à passer sous 400 Ko. Un PNG avec transparence
 * reste PNG. Retourne { fichier, largeur, hauteur }.
 */
export const compresserImage = (fichier, { bordMax = 1600, cible = 400 * 1024 } = {}) => new Promise((resolve) => {
  if (!fichier.type.startsWith('image/')) { resolve({ fichier }); return; }
  const url = URL.createObjectURL(fichier);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    let { width, height } = img;
    const ratio = Math.min(1, bordMax / Math.max(width, height));
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    const png = fichier.type === 'image/png';
    const type = png ? 'image/png' : 'image/jpeg';
    const nom = fichier.name.replace(/\.[^.]+$/, '') + (png ? '.png' : '.jpg');
    const essayer = (qualite) => {
      canvas.toBlob((blob) => {
        if (!blob) { resolve({ fichier, largeur: width, hauteur: height }); return; }
        if (!png && blob.size > cible && qualite > 0.5) { essayer(qualite - 0.1); return; }
        const sortie = blob.size < fichier.size ? new File([blob], nom, { type }) : fichier;
        resolve({ fichier: sortie, largeur: width, hauteur: height });
      }, type, qualite);
    };
    essayer(0.82);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve({ fichier }); };
  img.src = url;
});

/* ── Notifications navigateur ───────────────────────────────────────────── */

let contexteAudio = null;
export const sonDiscret = () => {
  try {
    contexteAudio = contexteAudio || new (window.AudioContext || window.webkitAudioContext)();
    const o = contexteAudio.createOscillator();
    const g = contexteAudio.createGain();
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.value = 0.04;
    o.connect(g); g.connect(contexteAudio.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.0001, contexteAudio.currentTime + 0.25);
    o.stop(contexteAudio.currentTime + 0.25);
  } catch { /* audio indisponible */ }
};

export const demanderPermissionNotification = () => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') Notification.requestPermission().catch(() => { });
};

export const notifierNavigateur = (titre, corps, onClick) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(titre, { body: corps, tag: 'cbconnect-conversation', silent: true });
    n.onclick = () => { window.focus(); onClick?.(); n.close(); };
  } catch { /* bloqué */ }
};

let titreOriginal = null;
export const compteurDansTitre = (n) => {
  if (titreOriginal == null) titreOriginal = document.title.replace(/^\(\d+\)\s*/, '');
  document.title = n > 0 ? `(${n}) ${titreOriginal}` : titreOriginal;
};

export const genererUuid = () => (crypto.randomUUID ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  }));
